import { useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useUserActivitySummary } from '@/hooks/useUserActivitySummary';
import type { UserSegment } from '@/hooks/useUserActivitySummary';
import { useUserFollows, useClubFollows } from '@/hooks/useFollows';
import type { Sport } from '@/types';

export type { UserSegment };

export type SectionType =
  | 'hero'
  | 'sportFilter'
  | 'upcomingSessions'
  | 'urgentMatches'
  | 'nearbyMatches'
  | 'clubActivities'
  | 'classes'
  | 'courts'
  | 'players'
  | 'clubs'
  | 'rankShowcase';

export type PrimaryCTA = 'find_match' | 'book_court' | 'join_class' | 'explore' | 'view_schedule';

export interface SectionConfig {
  visible: boolean;
  priority: number;
  limit: number;
}

export interface HomepagePersonalization {
  userSegment: UserSegment;
  isLoading: boolean;
  
  // Dynamic section ordering
  sectionOrder: SectionType[];
  sectionConfigs: Record<SectionType, SectionConfig>;
  
  // Sport prioritization
  sportPriority: Sport[];
  defaultSportFilter: Sport | 'all';
  
  // Recommendations
  showFollowingSuggestions: boolean;
  showSportOnboarding: boolean;
  primaryCTA: PrimaryCTA;
  ctaMessage: string;
  
  // Contextual data
  hasUpcomingSessions: boolean;
  hasActiveEnrollments: boolean;
  isNewUser: boolean;
  
  // Helper functions
  calculateMatchPriority: (match: any) => number;
  calculateClassPriority: (cls: any) => number;
}

// Section order by user segment
const SECTION_ORDER_BY_SEGMENT: Record<UserSegment, SectionType[]> = {
  new_user: ['hero', 'sportFilter', 'nearbyMatches', 'classes', 'players', 'clubs', 'rankShowcase'],
  match_player: ['hero', 'sportFilter', 'urgentMatches', 'nearbyMatches', 'classes', 'courts', 'clubs'],
  training_student: ['hero', 'upcomingSessions', 'classes', 'nearbyMatches', 'courts', 'players'],
  court_booker: ['hero', 'sportFilter', 'courts', 'nearbyMatches', 'classes', 'clubs'],
  club_member: ['hero', 'clubActivities', 'sportFilter', 'nearbyMatches', 'classes', 'courts', 'players'],
  active_all: ['hero', 'sportFilter', 'urgentMatches', 'nearbyMatches', 'classes', 'courts', 'clubs'],
};

// Section limits by segment (more items for preferred sections)
const SECTION_LIMITS_BY_SEGMENT: Record<UserSegment, Partial<Record<SectionType, number>>> = {
  new_user: { nearbyMatches: 5, classes: 3, players: 5, clubs: 3 },
  match_player: { urgentMatches: 5, nearbyMatches: 5, classes: 2, courts: 3 },
  training_student: { upcomingSessions: 5, classes: 5, nearbyMatches: 3, courts: 3 },
  court_booker: { courts: 5, nearbyMatches: 4, classes: 2, clubs: 2 },
  club_member: { clubActivities: 5, nearbyMatches: 4, classes: 3, courts: 2 },
  active_all: { urgentMatches: 3, nearbyMatches: 5, classes: 3, courts: 3, clubs: 2 },
};

// CTA messages by segment
const CTA_CONFIG: Record<UserSegment, { cta: PrimaryCTA; message: string }> = {
  new_user: { cta: 'explore', message: 'Khám phá cộng đồng thể thao quanh bạn!' },
  match_player: { cta: 'find_match', message: 'Tìm trận đấu gần bạn ngay!' },
  training_student: { cta: 'view_schedule', message: 'Xem lịch học của bạn' },
  court_booker: { cta: 'book_court', message: 'Đặt sân nhanh chóng!' },
  club_member: { cta: 'find_match', message: 'Tham gia hoạt động CLB!' },
  active_all: { cta: 'find_match', message: 'Sẵn sàng thi đấu chưa?' },
};

// Cache key for localStorage
const PERSONALIZATION_CACHE_KEY = 'homepage_personalization';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface CachedPersonalization {
  timestamp: number;
  segment: UserSegment;
  sportPriority: Sport[];
}

function getCachedData(): CachedPersonalization | null {
  try {
    const cached = localStorage.getItem(PERSONALIZATION_CACHE_KEY);
    if (!cached) return null;
    
    const data = JSON.parse(cached) as CachedPersonalization;
    if (Date.now() - data.timestamp > CACHE_DURATION) {
      localStorage.removeItem(PERSONALIZATION_CACHE_KEY);
      return null;
    }
    
    return data;
  } catch {
    return null;
  }
}

function setCachedData(segment: UserSegment, sportPriority: Sport[]) {
  try {
    const data: CachedPersonalization = {
      timestamp: Date.now(),
      segment,
      sportPriority,
    };
    localStorage.setItem(PERSONALIZATION_CACHE_KEY, JSON.stringify(data));
  } catch {
    // Ignore localStorage errors
  }
}

export function useHomepagePersonalization(): HomepagePersonalization {
  const { profile } = useAuth();
  const { data: activitySummary, isLoading } = useUserActivitySummary();
  const { followingUsers } = useUserFollows();
  const { followingClubs } = useClubFollows();
  
  // Use cached data for initial render
  const [cachedData] = useState(() => getCachedData());
  
  const personalization = useMemo(() => {
    // Determine user segment
    const userSegment: UserSegment = activitySummary?.userSegment ?? cachedData?.segment ?? 'new_user';
    
    // Calculate sport priority
    const sportPriority: Sport[] = [];
    
    // 1. Add primary activity sport (most played)
    if (activitySummary?.primaryActivitySport) {
      sportPriority.push(activitySummary.primaryActivitySport);
    }
    
    // 2. Add ranked sport
    if (activitySummary?.rankedSport && !sportPriority.includes(activitySummary.rankedSport)) {
      sportPriority.push(activitySummary.rankedSport);
    }
    
    // 3. Add profile preferred sports
    if (profile?.sports) {
      const profileSports = profile.sports as Sport[];
      profileSports.forEach(sport => {
        if (!sportPriority.includes(sport)) {
          sportPriority.push(sport);
        }
      });
    }
    
    // Get section order for this segment
    const sectionOrder = SECTION_ORDER_BY_SEGMENT[userSegment];
    const sectionLimits = SECTION_LIMITS_BY_SEGMENT[userSegment];
    
    // Build section configs
    const sectionConfigs: Record<SectionType, SectionConfig> = {} as any;
    const allSections: SectionType[] = [
      'hero', 'sportFilter', 'upcomingSessions', 'urgentMatches', 'nearbyMatches',
      'clubActivities', 'classes', 'courts', 'players', 'clubs', 'rankShowcase'
    ];
    
    allSections.forEach((section, index) => {
      const orderIndex = sectionOrder.indexOf(section);
      sectionConfigs[section] = {
        visible: orderIndex !== -1,
        priority: orderIndex === -1 ? 999 : orderIndex,
        limit: sectionLimits[section] ?? 3,
      };
    });
    
    // Adjust visibility based on context
    if (activitySummary) {
      // Only show upcomingSessions if user has upcoming sessions
      sectionConfigs.upcomingSessions.visible = activitySummary.upcomingSessionCount > 0;
      
      // Only show clubActivities if user is in clubs
      sectionConfigs.clubActivities.visible = activitySummary.clubCount > 0;
    }
    
    // Get CTA config
    const ctaConfig = CTA_CONFIG[userSegment];
    
    // Determine default sport filter
    const defaultSportFilter: Sport | 'all' = sportPriority[0] ?? 'all';
    
    // Calculate match priority function
    const calculateMatchPriority = (match: any): number => {
      let score = 0;
      
      // Sport match: +30 if matches user's primary sport
      if (profile?.ranked_sport === match.sport) score += 30;
      else if (sportPriority.includes(match.sport)) score += 20;
      
      // Skill level match: +20 if similar skill
      if (profile?.skill_level === match.creator_skill_level) score += 20;
      
      // Following bonus: +25 if created by someone user follows
      if (followingUsers.includes(match.created_by)) score += 25;
      
      // Urgency bonus: matches starting soon get higher priority
      const matchDateTime = new Date(`${match.date}T${match.time}`);
      const hoursUntilMatch = (matchDateTime.getTime() - Date.now()) / (1000 * 60 * 60);
      if (hoursUntilMatch <= 2) score += 40;
      else if (hoursUntilMatch <= 6) score += 25;
      else if (hoursUntilMatch <= 12) score += 15;
      
      // Distance penalty: -5 per km (if distance available)
      if (match.distance) score -= Math.min(match.distance * 5, 50);
      
      return score;
    };
    
    // Calculate class priority function
    const calculateClassPriority = (cls: any): number => {
      let score = 0;
      
      // Sport match
      if (sportPriority.includes(cls.sport)) score += 25;
      
      // Skill level appropriate
      if (profile?.skill_level === cls.skill_level) score += 20;
      
      // Verified coach bonus
      if (cls.coach?.coach_profile?.is_verified) score += 15;
      
      // Location match
      if (profile?.location && cls.location?.includes(profile.location)) score += 20;
      
      // Available spots bonus (classes almost full are more urgent)
      const spotsLeft = cls.max_students - cls.current_students;
      if (spotsLeft <= 2) score += 20;
      else if (spotsLeft <= 5) score += 10;
      
      return score;
    };
    
    // Cache the results
    if (activitySummary) {
      setCachedData(userSegment, sportPriority);
    }
    
    return {
      userSegment,
      isLoading,
      sectionOrder,
      sectionConfigs,
      sportPriority,
      defaultSportFilter,
      showFollowingSuggestions: followingUsers.length < 5 && userSegment !== 'new_user',
      showSportOnboarding: userSegment === 'new_user' && (!profile?.sports || profile.sports.length === 0),
      primaryCTA: ctaConfig.cta,
      ctaMessage: ctaConfig.message,
      hasUpcomingSessions: (activitySummary?.upcomingSessionCount ?? 0) > 0,
      hasActiveEnrollments: (activitySummary?.activeEnrollmentCount ?? 0) > 0,
      isNewUser: userSegment === 'new_user',
      calculateMatchPriority,
      calculateClassPriority,
    };
  }, [activitySummary, profile, followingUsers, followingClubs, cachedData, isLoading]);
  
  return personalization;
}
